import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Overlays currently open, newest last. Only the top one reacts to Escape.
const overlayStack = [];

/**
 * Shared behaviour for modal-like overlays (Modal, Drawer, Lightbox):
 * Escape closes, page scroll is locked, focus moves inside and is trapped,
 * and focus returns to the trigger when the overlay closes.
 */
export function useOverlay(open, onClose, panelRef) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;

    const entry = {};
    overlayStack.push(entry);
    document.body.classList.add('ui-scroll-lock');

    const previouslyFocused = document.activeElement;
    const panel = panelRef.current;
    if (panel && !panel.contains(document.activeElement)) {
      panel.focus({ preventScroll: true });
    }

    const onKeyDown = e => {
      if (overlayStack[overlayStack.length - 1] !== entry) return;

      if (e.key === 'Escape') {
        e.stopPropagation();
        onCloseRef.current?.();
        return;
      }

      if (e.key === 'Tab' && panelRef.current) {
        const items = [...panelRef.current.querySelectorAll(FOCUSABLE)].filter(
          el => el.offsetParent !== null
        );
        if (!items.length) {
          e.preventDefault();
          return;
        }
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      overlayStack.splice(overlayStack.indexOf(entry), 1);
      if (!overlayStack.length) document.body.classList.remove('ui-scroll-lock');
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [open, panelRef]);
}

/**
 * Calls onDismiss on a click outside every given ref, or on Escape.
 * Used by dropdowns and popovers.
 */
export function useDismiss(refs, open, onDismiss) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;
  const refsRef = useRef(refs);
  refsRef.current = refs;

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = e => {
      const inside = refsRef.current.some(r => r.current && r.current.contains(e.target));
      if (!inside) onDismissRef.current?.();
    };
    const onKeyDown = e => {
      if (e.key === 'Escape') onDismissRef.current?.();
    };

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
}

/**
 * Fixed-position coordinates for a popover anchored to an element.
 * Rendering the popover in a portal with these styles keeps it from being
 * clipped by scrolling containers such as table wrappers.
 *
 * placement: 'top' | 'bottom' (flips when there is no room)
 * align:     'end' (right edges line up) | 'start' (left edges line up)
 */
export function useAnchorPosition(anchorRef, open, { placement = 'bottom', align = 'end', offset = 8 } = {}) {
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return undefined;
    }

    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const rect = anchor.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const viewportHeight = window.innerHeight;
      const spaceAbove = rect.top;
      const spaceBelow = viewportHeight - rect.bottom;

      let side = placement;
      if (side === 'top' && spaceAbove < 160 && spaceBelow > spaceAbove) side = 'bottom';
      if (side === 'bottom' && spaceBelow < 160 && spaceAbove > spaceBelow) side = 'top';

      const next = { position: 'fixed' };
      if (side === 'top') next.bottom = viewportHeight - rect.top + offset;
      else next.top = rect.bottom + offset;
      if (align === 'end') next.right = Math.max(8, viewportWidth - rect.right);
      else next.left = Math.max(8, rect.left);
      setStyle(next);
    };

    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef, placement, align, offset]);

  return style;
}

/** Joins class names, skipping falsy values. */
export function cx(...names) {
  return names.filter(Boolean).join(' ');
}
